import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'

import TextInput from '../../ui/TextInput/TextInput'
import TextArea from '../../ui/TextArea/TextArea'
import ActionButton from '../../ui/ActionButton/ActionButton'
import Dropdown from '../../ui/Dropdown/Dropdown'
import Checkbox from '../../ui/Checkbox/Checkbox'
import PrivacyModalLink from '../../home/PrivacyModalLink/PrivacyModalLink'

import mobileNumberInput from '../../../services/input/mobileNumberInput'
import getInquiryType from '../../../services/data/getInquiryType'
import media from '../../../services/generateMediaQueries'

const ButtonWrapper = styled.div`
  margin: 2rem 0 0;
`

const BlockButton = styled(ActionButton)`
  width: 100%;
`

const MobileAndTypeDiv = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  & > div {
    width: 100%;
  }

  & > div:nth-of-type(2) {
    output {
      display: block;
      min-height: 1.125rem;
    }
    output&:empty {
      height: 0;
      opacity: 0;
    }
  }

  ${media.greaterThan('small')`
    gap: 1rem;
    flex-direction: row;
  `};
`

const TermsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 0 0 100%;
  margin-bottom: 0.5rem;
`

const StyledCheckbox = styled(Checkbox)`
  border-radius: var(--border-radius-form);
  border: var(--border-form);
  margin-top: 0.1rem;
  margin-right: 1rem;
`

const TextDescription = styled.p`
  font-size: 1rem;
  line-height: 1.5rem;
  letter-spacing: 0.4px;
  margin: 0;
  text-align: left;
`

const StyledPrivacyModalLink = styled(PrivacyModalLink)`
  font-weight: var(--font-weight-boldest);
  &:hover {
    color: #d9c997;
  }
`

const Subtitle = styled.h3`
  font-weight: var(--font-weight-boldest);
  font-size: 24px;
  color: var(--color-primary, #5bbdaf);
  margin: 0;
`

const SubDescription = styled.p`
  margin: 0.5rem 0 1.5rem;
  line-height: 1.5;
`

function ContactUsForm(props) {
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    disabled,
    method,
    action,
  } = props

  const { isAcceptTerms, isCheckSubs } = values
  const INQUIRY_TYPE = getInquiryType()

  return (
    <>
      <div>
        <Subtitle>We want to hear from you!</Subtitle>
        <SubDescription>
          Let us know how we can make your experience amazing. We’ll get back to
          you soon.
        </SubDescription>
      </div>
      <form onSubmit={handleSubmit} method={method} action={action}>
        <fieldset disabled={isSubmitting || disabled}>
          <TextInput
            name="firstName"
            type="text"
            placeholder="First Name"
            maxLength={32}
            initialValue={values.firstName}
            error={errors.firstName}
            touched={touched.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextInput
            name="lastName"
            type="text"
            placeholder="Last Name"
            maxLength={32}
            initialValue={values.lastName}
            error={errors.lastName}
            touched={touched.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextInput
            name="email"
            type="text"
            placeholder="Email"
            maxLength={48}
            initialValue={values.email}
            error={errors.email}
            touched={touched.email}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          <MobileAndTypeDiv>
            <div>
              <TextInput
                name="mobileNumber"
                type="text"
                placeholder="Mobile Number"
                maxLength={11}
                initialValue={values.mobileNumber}
                error={errors.mobileNumber}
                touched={touched.mobileNumber}
                onChange={e => mobileNumberInput(e, handleChange)}
                onBlur={handleBlur}
              />
            </div>
            <div>
              <Dropdown
                name="type"
                placeholder="Select your topic"
                data-testid="contactUsType"
                initialValue={values.type}
                error={errors.type}
                touched={touched.type}
                options={INQUIRY_TYPE}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </MobileAndTypeDiv>
          <TextArea
            name="message"
            placeholder="Your Message"
            maxLength={10000}
            rows={4}
            initialValue={values.message}
            error={errors.message}
            touched={touched.message}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TermsContainer>
            <StyledCheckbox
              name="isAcceptTerms"
              checked={isAcceptTerms}
              onChange={handleChange}
            />
            <TextDescription>
              I have read and agree with the{' '}
              <StyledPrivacyModalLink zIndex={10001} isAyalaPN>
                Privacy Notice
              </StyledPrivacyModalLink>{' '}
              of Ayala Malls.
            </TextDescription>
          </TermsContainer>
          <TermsContainer>
            <StyledCheckbox
              name="isCheckSubs"
              checked={isCheckSubs}
              onChange={handleChange}
            />
            <TextDescription>
              I would like to subscribe to email and SMS updates from Ayala
              Malls.
            </TextDescription>
          </TermsContainer>

          <ButtonWrapper>
            <BlockButton
              type="submit"
              variant="primary"
              disabled={!isValid || !isAcceptTerms}
            >
              Submit
            </BlockButton>
          </ButtonWrapper>
        </fieldset>
      </form>
    </>
  )
}

ContactUsForm.defaultProps = {
  handleSubmit: null,
  handleChange: null,
  handleBlur: null,
  values: {},
  errors: {},
  touched: {},
  isSubmitting: false,
  isValid: false,
  disabled: false,
  method: 'get',
  action: '',
  status: {},
}

ContactUsForm.propTypes = {
  handleSubmit: PropTypes.func,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  values: PropTypes.shape(),
  errors: PropTypes.shape(),
  touched: PropTypes.shape(),
  isSubmitting: PropTypes.bool,
  isValid: PropTypes.bool,
  disabled: PropTypes.bool,
  method: PropTypes.oneOf(['get', 'post']),
  action: PropTypes.string,
  status: PropTypes.shape(),
}

export default ContactUsForm
